import { FC } from 'react';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import {
	ActionIcon,
	Box,
	Button,
	Divider,
	Drawer,
	Flex,
	LoadingOverlay,
	Text,
} from '@mantine/core';
import { creatorActions } from '@/store/slices/creatorSlice';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { ExportType } from '@/renderer/Exporter';
import { useMutation } from '@tanstack/react-query';
import ArrowLeftIcon from '@/assets/icons/arrowLeft.svg?react';
import ModelIcon from '@/assets/icons/blocks.svg?react';
import PdfIcon from '@/assets/icons/file-type-pdf.svg?react';
import ExcelIcon from '@/assets/icons/file-spreadsheet.svg?react';
import download from 'js-file-download';
import ProjectApi from '@/api/ProjectApi';
import AppApi from '@/api/AppApi';

const renderer = ProjectRenderer.getInstance();

export const DownloadDrawer: FC = () => {
	const dispatch = useTypedDispatch();

	const { options, project } = useTypedSelector((state) => state.creator);

	const handleClose = () => {
		dispatch(creatorActions.updateOptions({ downloadDrawerOpened: false }));
	};

	const handleDownloadCollection = () => {
		renderer.handleExportCollection(ExportType.GLTF);
	};

	const handleDownloadPDF = async () => {
		const blob = await renderer.screenshot();
		const attachment = await AppApi.createAttachment({ file: blob });

		await ProjectApi.update({
			id: project.id,
			image: attachment,
		});

		const pdfBlob = await ProjectApi.exportFile({
			id: project.id,
			type: 'pdf',
		});

		return pdfBlob;
	};

	const getPDFMutation = useMutation({
		mutationFn: handleDownloadPDF,
		onSuccess: (blob) => {
			download(blob, `Konfiguracja projektu - Moje Bambino.pdf`);
		},
	});

	const getXLSXMutation = useMutation({
		mutationFn: ProjectApi.exportFile,
		onSuccess: (blob) => {
			download(blob, `Konfiguracja projektu - Moje Bambino.xlsx`);
		},
	});

	const handleDownload = (type: 'pdf' | 'xlsx') => () => {
		switch (type) {
			case 'pdf':
				getPDFMutation.mutate();
				break;
			case 'xlsx':
				getXLSXMutation.mutate({
					id: project.id,
					type: 'xlsx',
				});
				break;

			default:
				throw new Error('INVALID_EXPORT_TYPE');
		}
	};

	return (
		<Drawer
			opened={options.downloadDrawerOpened}
			onClose={handleClose}
			title={
				<Flex gap="sm">
					<ActionIcon onClick={handleClose} variant="transparent">
						<ArrowLeftIcon />
					</ActionIcon>
					<Text size="lg" fw="bold">
						Podsumowanie konfiguracji
					</Text>
				</Flex>
			}
		>
			<Box pos="relative">
				<LoadingOverlay
					visible={getPDFMutation.isPending || getXLSXMutation.isPending}
				/>
				<Divider my="md" />
				<Flex gap="md" direction="column">
					<Text size="sm" fw="bold" mb="xs">
						Jaki plik do pobrania chciałbys otrzymać?
					</Text>
					<Button
						color="black"
						size="md"
						leftSection={<ModelIcon stroke="white" />}
						fullWidth
						onClick={handleDownloadCollection}
					>
						Pobierz jako obiekt 3D
					</Button>
					<Button
						color="black"
						size="md"
						leftSection={<PdfIcon stroke="white" />}
						fullWidth
						onClick={handleDownload('pdf')}
					>
						Pobierz jako PDF
					</Button>
					<Button
						color="black"
						size="md"
						leftSection={<ExcelIcon stroke="white" />}
						fullWidth
						onClick={handleDownload('xlsx')}
					>
						Pobierz jako Excel
					</Button>
				</Flex>
				<Divider my="md" />
				{/* <Button
					onClick={handleProjectInquirySubmit}
					color="black"
					size="md"
					leftSection={<ContactIcon stroke="white" />}
					fullWidth
				>
					Zapytaj o produkt
				</Button> */}
			</Box>
		</Drawer>
	);
};
